import { useCallback, useMemo, useState } from 'react';
import { useTranslation, i18n } from '@tasama/i18n';
import {
  useApiMutation,
  useFileUpload,
  ApiError,
} from '@tasama/api-client-query';
import {
  useStepManagementStore,
  type StepManagementStore,
} from '../stores/step-management.store';
import {
  useYourDetailsStore,
  type YourDetailsStore,
} from '../../features/your-details/stores/your-details.store';
import type { OnboardingStepData, OnboardingStepResponse } from '../../types';
import {
  useCompanyDetailsStore,
  type CompanyDetailsStore,
} from '../../features/company-details/stores/company-details.store';
import { useSomeThingWentWrongModalStore } from '@tasama/ui';
import {
  useUploadDocumentsStore,
  uploadedFileRegistry,
} from '../../features/upload-documents/stores/upload-documents.store';
import {
  useProjectDetailsStore,
  type ProjectDetailsStore,
} from '../../features/project-details';

/**
 * Enhanced error interface for better error handling
 */
export interface OnboardingApiError {
  message: string;
  type: 'error' | 'warning' | 'unauthorized';
  code?: string | number | undefined;
  details?: Record<string, unknown> | undefined;
}

/**
 * Transform API error to OnboardingApiError with proper categorization
 */
const transformError = (err: ApiError): OnboardingApiError => {
  // Determine error type based on status code or message
  let type: OnboardingApiError['type'] = 'error';

  if (err.code === 401 || err.code === 'UNAUTHORIZED') {
    type = 'unauthorized';
  } else if (
    err.code === 409 ||
    err.message.toLowerCase().includes('already exists')
  ) {
    type = 'warning';
  } else if (err.code === 400 || err.code === 422) {
    type = 'warning';
  }

  return {
    message: err.key ? i18n.t(err.key, { defaultValue: err.key }) : err.message,
    type,
    code: err.code,
    details: err.details,
  };
};

/**
 * Check if error is a company details validation error
 */
const isHavingValidationError = (err: any): boolean => {
  return (
    err.details?.validationErrors &&
    Array.isArray(err.details.validationErrors) &&
    err.details.validationErrors.length > 0
  );
};

/**
 * Step-specific error handlers interface
 * Defines the contract for error handling methods per step
 */
interface StepErrorHandlers {
  setFieldError: (_field: string, _message: string) => void;
  markFieldAsInteracted: (_field: string) => void;
  setValidity: (_isValid: boolean) => void;
  fieldName: string;
  message: string;
}

/**
 * Generic validation error handler for all onboarding steps
 * Handles validation errors by mapping fields and setting errors in store
 * Follows Single Responsibility Principle - only handles validation errors
 */
const handleValidationError = (
  err: any,
  stepErrorHandler: StepErrorHandlers,
  onFieldNotFound?: () => void
): void => {
  const validationErrors = err.details.validationErrors;

  validationErrors.forEach((validationError: any) => {
    const fieldName = validationError?.field;
    const errorMessage = validationError?.constraints?.[0];

    if (!fieldName || !errorMessage) {
      return;
    }

    try {
      stepErrorHandler.markFieldAsInteracted(fieldName);
      stepErrorHandler.setFieldError(fieldName, errorMessage);
    } catch {
      if (onFieldNotFound) {
        onFieldNotFound();
      }
    }
  });

  // Set form validity to false to disable submit button after processing all errors
  stepErrorHandler.setValidity(false);
};

/**
 * @param err - API error object
 * @param stepId - Current onboarding step identifier
 * @param stepErrorHandlerRegistry - Map of step-specific error handlers indexed by step ID or error code
 * @param onRetry - Callback function to show retry modal
 */
const handleOnboardingError = (
  err: any,
  stepId: string,
  stepErrorHandlerRegistry: Record<string, StepErrorHandlers>,
  onRetry: () => void
): void => {
  // Handle specific 409 conflict errors
  if (err.code === 409) {
    // First try to get handler by error code from details
    const errorCode: string = err.details?.code || '';
    let conflictErrorHandler = stepErrorHandlerRegistry[errorCode];

    // If no handler found by error code, try to use the step ID
    if (!conflictErrorHandler && stepId) {
      conflictErrorHandler = stepErrorHandlerRegistry[stepId];
    }

    if (conflictErrorHandler) {
      // Check if there's a field name in the error details to override the default
      const fieldNameFromError =
        err.details?.field || conflictErrorHandler.fieldName;

      // Prefer BE-supplied i18n key; i18n.t returns the key itself when no
      // translation is found, so the user always sees a meaningful string.
      const resolvedMessage = err.key
        ? i18n.t(err.key, { defaultValue: err.key })
        : err.message || conflictErrorHandler.message;

      conflictErrorHandler.markFieldAsInteracted(fieldNameFromError);
      conflictErrorHandler.setFieldError(fieldNameFromError, resolvedMessage);
      conflictErrorHandler.setValidity(false);
      return;
    }
  }

  // Handle validation errors with step-specific handlers
  if (isHavingValidationError(err)) {
    const validationErrorHandler = stepErrorHandlerRegistry[stepId];
    if (validationErrorHandler) {
      handleValidationError(err, validationErrorHandler, onRetry);
      return;
    }
  }

  // Default error handling - show retry modal
  onRetry();
};

/**
 * Custom hook for handling onboarding API operations
 * Uses React Query mutations with dynamic endpoint support
 */
export const useOnboardingApi = () => {
  const { t } = useTranslation('onboarding');
  const { getCurrentStepInfo } = useStepManagementStore(
    (s: StepManagementStore) => s
  );

  // Your Details store methods - use separate selectors to avoid infinite re-renders
  const setYourDetailsFieldError = useYourDetailsStore(
    (state: YourDetailsStore) => state.setFieldError
  );
  const markYourDetailsFieldAsInteracted = useYourDetailsStore(
    (state: YourDetailsStore) => state.markFieldAsInteracted
  );
  const setYourDetailsValidity = useYourDetailsStore(
    (state: YourDetailsStore) => state.setValidity
  );

  // Company Details store methods - use separate selectors to avoid infinite re-renders
  const setCompanyDetailsFieldError = useCompanyDetailsStore(
    (state: CompanyDetailsStore) => state.setFieldError
  );
  const markCompanyDetailsFieldAsInteracted = useCompanyDetailsStore(
    (state: CompanyDetailsStore) => state.markFieldAsInteracted
  );
  const setCompanyDetailsValidity = useCompanyDetailsStore(
    (state: CompanyDetailsStore) => state.setValidity
  );

  const setProjectDetailsFieldError = useProjectDetailsStore(
    (state: ProjectDetailsStore) => state.setFieldError
  );

  const { open: openSomethingWentWrongModal } =
    useSomeThingWentWrongModalStore();

  const [error, setError] = useState<OnboardingApiError | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  // Generate endpoint dynamically based on current step
  const getEndpoint = useCallback(() => {
    const currentStep = getCurrentStepInfo();
    if (!currentStep) {
      throw new Error('Current step information not available');
    }
    return `/onboarding/steps/${currentStep.id}`;
  }, [getCurrentStepInfo]);

  // Configure error code to handler mapping for conflict and validation errors (Dependency Injection)
  const errorCodeToHandlerMap: Record<string, StepErrorHandlers> = useMemo(
    () =>
      ({
        // Map by step ID for 409 conflicts
        'company-details': {
          setFieldError: setCompanyDetailsFieldError,
          markFieldAsInteracted: markCompanyDetailsFieldAsInteracted,
          setValidity: setCompanyDetailsValidity,
          fieldName: 'registrationNumber',
          message: t('errors.crNumberAlreadyRegistered'),
        },
        'your-details': {
          setFieldError: setYourDetailsFieldError,
          markFieldAsInteracted: markYourDetailsFieldAsInteracted,
          setValidity: setYourDetailsValidity,
          fieldName: 'email',
          message: t('errors.emailAlreadyRegistered'),
        },
        'project-details': {
          setFieldError: setProjectDetailsFieldError,
          markFieldAsInteracted: () => {}, // Project details doesn't have this method
          setValidity: () => {}, // Project details doesn't have this method
          fieldName: 'projectCodeName',
          message: t('errors.projectAlreadyExists'),
        },
        // Additional mappings for specific error codes if API returns them
        EMAIL_ALREADY_EXISTS: {
          setFieldError: setYourDetailsFieldError,
          markFieldAsInteracted: markYourDetailsFieldAsInteracted,
          setValidity: setYourDetailsValidity,
          fieldName: 'email',
          message: t('errors.emailAlreadyRegistered'),
        },
        REGISTRATION_NUMBER_EXISTS: {
          setFieldError: setCompanyDetailsFieldError,
          markFieldAsInteracted: markCompanyDetailsFieldAsInteracted,
          setValidity: setCompanyDetailsValidity,
          fieldName: 'registrationNumber',
          message: t('errors.crNumberAlreadyRegistered'),
        },
        PROJECT_CODE_EXISTS: {
          setFieldError: setProjectDetailsFieldError,
          markFieldAsInteracted: () => {}, // Project details doesn't have this method
          setValidity: () => {}, // Project details doesn't have this method
          fieldName: 'projectCodeName',
          message: t('errors.projectAlreadyExists'),
        },
      }) as Record<string, StepErrorHandlers>,
    [
      setCompanyDetailsFieldError,
      markCompanyDetailsFieldAsInteracted,
      setCompanyDetailsValidity,
      setYourDetailsFieldError,
      markYourDetailsFieldAsInteracted,
      setYourDetailsValidity,
      setProjectDetailsFieldError,
    ]
  );

  // Mutation for regular data submission (POST)
  const submitMutation = useApiMutation<
    OnboardingStepResponse,
    OnboardingStepData
  >(getEndpoint, 'POST', {
    onSuccess: () => {
      // Step submission response received
      setError(null);
    },
    onError: (err: any) => {
      // Error submitting step data
      const currentStep = getCurrentStepInfo();
      const stepId = currentStep?.id ?? '';

      // Use centralized error handler
      handleOnboardingError(
        err,
        stepId,
        errorCodeToHandlerMap,
        openSomethingWentWrongModal
      );
    },
  });

  // Mutation for file uploads
  const uploadMutation = useFileUpload<OnboardingStepResponse>(getEndpoint, {
    onProgress: (progress: number) => {
      setUploadProgress(progress);
    },
    onSuccess: () => {
      // File upload response received
      setError(null);
      setUploadProgress(0);
    },
    onError: () => {
      // Route upload errors inline to the upload-documents store; skip global toast
      useUploadDocumentsStore
        .getState()
        .setUploadNetworkError(t('errors.uploadFailed'));
      uploadedFileRegistry.clear();
      setUploadProgress(0);
    },
  });

  // Mutation for updating data (PATCH)
  const updateMutation = useApiMutation<
    OnboardingStepResponse,
    Partial<OnboardingStepData>
  >(getEndpoint, 'PATCH', {
    onError: (err: any) => {
      // Error updating step data
      const apiError = transformError(err);
      setError(apiError);
    },
  });

  /**
   * Submit step data without navigation (navigation handled by Layout component)
   */
  const submitStepData = useCallback(
    async (data: OnboardingStepData | FormData): Promise<boolean> => {
      const currentStep = getCurrentStepInfo();

      if (!currentStep) {
        // Current step information not available
        setError({
          message: t('errors.stepNotFound'),
          type: 'error',
          code: 'STEP_NOT_FOUND',
        });
        return false;
      }

      // Submitting to endpoint for step

      try {
        // Check if this is a file upload step
        if (data instanceof FormData) {
          await uploadMutation.mutateAsync(data);
        } else {
          await submitMutation.mutateAsync(data);
        }
        return true;
      } catch {
        return false;
      }
    },
    [getCurrentStepInfo, submitMutation, uploadMutation]
  );

  /**
   * Update step data without navigation
   */
  const updateStepData = useCallback(
    async (data: Partial<OnboardingStepData>): Promise<boolean> => {
      const currentStep = getCurrentStepInfo();

      if (!currentStep) {
        // Current step information not available
        setError({
          message: t('errors.stepNotFound'),
          type: 'error',
          code: 'STEP_NOT_FOUND',
        });
        return false;
      }

      try {
        await updateMutation.mutateAsync(data);
        return true;
      } catch {
        return false;
      }
    },
    [getCurrentStepInfo, updateMutation]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isSubmitting = useMemo(
    () =>
      submitMutation.isPending ||
      uploadMutation.isPending ||
      updateMutation.isPending,
    [
      submitMutation.isPending,
      uploadMutation.isPending,
      updateMutation.isPending,
    ]
  );

  return {
    // State
    isSubmitting,
    error,
    uploadProgress,

    // Actions
    submitStepData,
    updateStepData,
    clearError,
  };
};

export default useOnboardingApi;
